import type { DiscountBanner, StockInfo } from './accessors';
import type { ProductLabel } from './productLabel';
import type { DateSerializable } from './stockInfo';

import type { StockStatusT } from '@vs-shop/domain/stock';
import { coalesceNonEmpty } from '@vs-shop/domain/string/coalesceNonEmpty';
import { Option } from 'effect';

import { getDiscountBanner, getMainImagePath, getStockInfo } from './accessors';

export type HasProductFields = {
  readonly code: string;
  readonly routerUrl: string;
  readonly ageRestriction: 'none' | '18+';
  readonly name?: string | null | undefined;
  readonly metaTitle?: string | null | undefined;
  readonly slug?: string | null | undefined;
  readonly shortDescription?: string | null | undefined;
  readonly subtitle?: string | null | undefined;
  readonly mainImage?: { readonly pathAbstract?: string | null | undefined } | null | undefined;
};

export type HasVariantFields = {
  readonly price?: number | undefined;
  readonly priceWithoutTax?: number | undefined;
  readonly originalPrice?: number | null | undefined;
  readonly isFavourite?: boolean | undefined;
  readonly available?: boolean | undefined;
  readonly stockStatus?: StockStatusT | undefined;
  readonly maxOrderable?: number | null | undefined;
  readonly dispatchDate?: DateSerializable | null | undefined;
  readonly localWarehouse?:
    | readonly {
        readonly type?: string | null | undefined;
        readonly quantity: number | null;
        readonly dispatchDate?: DateSerializable | null | undefined;
      }[]
    | undefined;
  readonly couponCodes?:
    | readonly {
        readonly promotedPrice: number;
        readonly code: string;
        readonly style: 'none' | 'clickable' | 'static';
      }[]
    | undefined;
};

export type ProductCardData = {
  name: string;
  metaTitle: string;
  slug: string;
  code: string;
  routerUrl: string;
  imagePath: string | null;
  description: string;
  subtitle: string | null;
  /** `None` when the variant carries no channel price — never fabricated as `0`. */
  price: Option.Option<number>;
  /** `None` when the variant carries no channel price — never fabricated as `0`. */
  priceWithoutTax: Option.Option<number>;
  originalPrice: number | null;
  stock: StockInfo;
  discountBanner: DiscountBanner | null;
  labels: readonly ProductLabel[];
  isFavourite: boolean;
  ageRestriction: 'none' | '18+';
  category: string | null;
};

/** The two money fields every card-shaped view model in this package carries as `Option`. */
export type HasCardPrices = {
  readonly price: Option.Option<number>;
  readonly priceWithoutTax: Option.Option<number>;
};

/**
 * `T` with `price`/`priceWithoutTax` resolved to plain numbers, absence and
 * genuine zero both landing on `0`.
 *
 * An Effect `Some` is a class instance carrying `toJSON`, and RSC rejects a
 * non-plain prototype crossing into a `'use client'` component (rule 11), so a
 * value that crosses that boundary crosses in this shape. The parameter is the
 * card-shaped view model, not one named alias: `ProductCardData`,
 * `AccessoryCardData` and the narrow read types derived from them all cross
 * the same boundary and all carry the same two fields.
 */
export type ResolvedCardPrices<T extends HasCardPrices> = Omit<T, 'price' | 'priceWithoutTax'> & {
  price: number;
  priceWithoutTax: number;
};

/** `ProductCardData` as it crosses to a client component. */
export type ClientProductCardData = ResolvedCardPrices<ProductCardData>;

/**
 * Resolve a card's money for the RSC boundary.
 *
 * `Option.getOrElse(() => 0)` is what every consumer downstream of a client
 * seam already did itself — the GTM select-item payload, both cart payloads,
 * the bundle line's price copy — so resolving here moves that call, it does
 * not change a value. It is NOT correct everywhere: `ProductCardPriceStock`
 * and `ProductCardStickers` still read the `Option` and give absence its own
 * no-price rendering (issue 219), so a seam whose card tree includes them
 * keeps the render on the server side of the boundary instead of calling this.
 */
export const resolveClientProductCardData = <T extends HasCardPrices>({
  price,
  priceWithoutTax,
  ...rest
}: T): ResolvedCardPrices<T> => ({
  ...rest,
  price: Option.getOrElse(price, () => 0),
  priceWithoutTax: Option.getOrElse(priceWithoutTax, () => 0),
});

export function toProductCard(
  product: HasProductFields,
  variant: (HasVariantFields & { readonly labels?: readonly ProductLabel[] | undefined }) | null
): ProductCardData {
  return {
    name: product.name ?? '',
    metaTitle: coalesceNonEmpty(product.metaTitle, product.name) ?? '',
    slug: product.slug ?? '',
    code: product.code,
    routerUrl: product.routerUrl,
    imagePath: getMainImagePath(product),
    description: product.shortDescription ?? '',
    subtitle: product.subtitle ?? null,
    price: Option.fromNullishOr(variant?.price),
    priceWithoutTax: Option.fromNullishOr(variant?.priceWithoutTax),
    originalPrice: variant?.originalPrice ?? null,
    stock: getStockInfo(variant),
    discountBanner: getDiscountBanner(variant),
    labels: variant?.labels ?? [],
    isFavourite: variant?.isFavourite ?? false,
    ageRestriction: product.ageRestriction,
    category: null,
  };
}
